
import { Coordinates } from "../types";

export let lastElevationDebug = {
  lastUrl: "",
  lastResponse: null as any,
  lastError: null as any,
  timestamp: 0
};

/**
 * Fetch elevations for multiple coordinates in batch using Open-Meteo Elevation API.
 * Free, no API key, supports up to ~300 coords per request.
 * Returns array of elevations (meters) matching input order, or null on failure.
 */
export const fetchBatchElevations = async (coords: { latitude: number; longitude: number }[]): Promise<number[] | null> => {
  if (!coords.length) return null;
  // Open-Meteo URL length limit ~2000 chars; ~100 points per request
  const CHUNK_SIZE = 100;
  const results: number[] = [];

  for (let i = 0; i < coords.length; i += CHUNK_SIZE) {
    const chunk = coords.slice(i, i + CHUNK_SIZE);
    const lats = chunk.map(c => c.latitude.toFixed(5)).join(',');
    const lons = chunk.map(c => c.longitude.toFixed(5)).join(',');
    const url = `https://api.open-meteo.com/v1/elevation?latitude=${lats}&longitude=${lons}`;
    try {
      const res = await fetch(url);
      if (!res.ok) return null;
      const data = await res.json();
      if (!data.elevation || !Array.isArray(data.elevation)) return null;
      results.push(...data.elevation.map((e: number) => Math.round(e)));
    } catch {
      return null;
    }
  }
  return results;
};

export const fetchElevation = async (coords: Coordinates): Promise<number | null> => {
  if (!coords || typeof coords.latitude !== 'number' || typeof coords.longitude !== 'number') {
    lastElevationDebug.lastError = "Ugyldige koordinater sendt til fetchElevation";
    return null;
  }

  // Geonorge krever ofte høy presisjon for punkt-oppslag
  const lat = coords.latitude.toFixed(6);
  const lon = coords.longitude.toFixed(6);
  const url = `https://ws.geonorge.no/hoydedata/v1/punkt?nord=${lat}&ost=${lon}&koordsys=4326`;
  
  lastElevationDebug.lastUrl = url;
  lastElevationDebug.timestamp = Date.now();

  try {
    const response = await fetch(url);
    if (!response.ok) {
      const err = `API Error: ${response.status} ${response.statusText}`;
      lastElevationDebug.lastError = err;
      return null;
    }
    
    const data = await response.json();
    lastElevationDebug.lastResponse = data;
    
    // Geonorge returnerer nå et objekt med en 'punkter' array
    // Vi sjekker både den nye array-baserte strukturen og den gamle flate strukturen for sikkerhets skyld
    let elevationValue: number | undefined;

    if (data && Array.isArray(data.punkter) && data.punkter.length > 0) {
      elevationValue = data.punkter[0].z;
    } else if (data && typeof data.z === 'number') {
      elevationValue = data.z;
    }
    
    if (typeof elevationValue === 'number') {
      if (elevationValue === 0) {
        console.warn("Elevation returned exactly 0 at", lat, lon);
      }
      return Math.round(elevationValue);
    }
    
    lastElevationDebug.lastError = "Ingen 'z'-verdi funnet i 'punkter' array eller i rot-objektet";
    return null;
  } catch (error: any) {
    lastElevationDebug.lastError = error.message;
    console.error("Elevation fetch failed:", error);
    return null;
  }
};
